import { Typography, useTheme, Chip, Box, type SxProps, type Theme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Palette } from 'src/feature/theme/model/Palette'
import { RatingChart } from '../../model/my-team-skill-gaps'

export type CriticalGapChartProps = {
  chartData?: RatingChart[]
  isLoading?: boolean
  onThresholdChange?: (threshold: number) => void
  onItemClick?: (itemName: string) => void
}

type TooltipProps = {
  active?: boolean
  payload?: Array<{ payload: RatingChart }>
}

const THRESHOLD_OPTIONS = [
  { value: 50, label: '50%' },
  { value: 70, label: '70%' },
  { value: 90, label: '90%' },
] as const

const CHART_HEIGHT = 200

function CustomTooltip({ active, payload }: TooltipProps) {
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam

  if (active && payload && payload.length) {
    const data = payload[0].payload
    const gap = (data.avgExpectedSkillRating || 0) - (data.avgSkillRating || 0)

    return (
      <AppBox sx={styles.tooltip}>
        <Typography sx={styles.tooltipTitle}>{data.name}</Typography>
        <Typography sx={styles.tooltipValue}>
          {pageTranslation.criticalGapChart.gap()}: {gap.toFixed(2)} {pageTranslation.criticalGapChart.points()}
        </Typography>
        <Typography sx={styles.tooltipDetail}>
          {pageTranslation.donutChart.current()}: {(data.avgSkillRating || 0).toFixed(2)} / {pageTranslation.donutChart.expected()}: {(data.avgExpectedSkillRating || 0).toFixed(2)}
        </Typography>
      </AppBox>
    )
  }

  return null
}

export const CriticalGapChart = observer(({ chartData, isLoading = false, onThresholdChange, onItemClick }: CriticalGapChartProps) => {
  const theme = useTheme()
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam
  const [threshold, setThreshold] = useState(50)

  const handleThresholdChange = (newThreshold: number) => {
    setThreshold(newThreshold)
    onThresholdChange?.(newThreshold)
  }

  const skills = chartData || []

  if (isLoading) {
    return (
      <AppPaper sx={styles.root}>
        <AppBox sx={styles.box}>
          <AppSkeleton
            count={1}
            direction="column"
            rects={[{ width: '100%', height: CHART_HEIGHT, color: 'background.dark' }]}
          />
        </AppBox>
      </AppPaper>
    )
  }

  return (
    <AppPaper sx={styles.root}>
      <AppBox sx={styles.filterContainer}>
        <Typography sx={styles.filterTitle}>{pageTranslation.metricsHeader.criticalGaps()}</Typography>
        <AppBox sx={styles.chipsContainer}>
          {THRESHOLD_OPTIONS.map(option => (
            <Chip
              key={option.value}
              label={option.label}
              onClick={() => handleThresholdChange(option.value)}
              variant={threshold === option.value ? 'filled' : 'outlined'}
              color={threshold === option.value ? 'primary' : 'default'}
              sx={styles.chip}
            />
          ))}
        </AppBox>
        <Typography sx={styles.filterDescription}>
          {pageTranslation.donutChart.showing()} {skills.length} {skills.length === 1 ? pageTranslation.donutChart.skill() : pageTranslation.donutChart.skills()} {pageTranslation.criticalGapChart.where()} <Box component="span" sx={styles.thresholdValue}>≥{threshold}%</Box> {pageTranslation.criticalGapChart.ofUsersHaveCriticalGap()}
        </Typography>
      </AppBox>

      {skills.length === 0 ? (
        <AppBox sx={styles.emptyState}>
          <Typography sx={styles.noDataText}>
            {pageTranslation.criticalGapChart.noSkillsFound()} ≥{threshold}% {pageTranslation.criticalGapChart.ofUsersHaveCriticalGap()}
          </Typography>
        </AppBox>
      ) : (
        <AppBox sx={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart
              data={skills}
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              onClick={(data) => {
                if (data?.activePayload?.[0]?.payload?.name && onItemClick) {
                  onItemClick(data.activePayload[0].payload.name)
                }
              }}
              style={{ cursor: onItemClick ? 'pointer' : 'default' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} opacity={0.3} />
              <XAxis
                dataKey="name"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: 9 }}
                textAnchor="middle"
                height={40}
                interval={0}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: 10 }}
                domain={[0, 5]}
                ticks={[0, 1, 2, 3, 4, 5]}
                label={{ value: pageTranslation.donutChart.rating(), angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 10, fill: theme.palette.text.secondary, textAnchor: 'middle' } }}
              />
              <Tooltip content={<CustomTooltip />} />
              
              <Line
                type="monotone"
                dataKey="avgExpectedSkillRating"
                stroke={Palette.OLIVE_GREEN}
                strokeWidth={1.5}
                dot={{ fill: Palette.OLIVE_GREEN, r: 4 }}
                activeDot={{ r: 6 }}
                name={pageTranslation.donutChart.expected()}
              />
              
              <Line
                type="monotone"
                dataKey="avgSkillRating"
                stroke={Palette.DANGER}
                strokeWidth={2}
                dot={{ fill: Palette.DANGER, r: 4 }}
                activeDot={{ r: 6 }}
                name={pageTranslation.donutChart.current()}
              />
            </LineChart>
          </ResponsiveContainer>
        </AppBox>
      )}
    </AppPaper>
  )
})

const styles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    width: '100%',
    padding: '10px 15px',
    borderRadius: '10px',
    flexDirection: 'column',
    gap: 10,
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: 10,
    borderRadius: '10px',
  },
  filterTitle: {
    fontSize: 11,
    color: 'text.secondary',
  },
  chipsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  chip: {
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    padding: { xs: '3px 2px', sm: '4px 2px' },
    height: 'auto',
    fontSize: { xs: 10, sm: 11 },
    fontWeight: 500,
    '& .MuiChip-label': {
      fontSize: 11,
      fontWeight: 500,
    },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  filterDescription: {
    fontSize: 11,
    color: 'text.secondary',
    textAlign: 'center',
  },
  thresholdValue: {
    fontWeight: 700,
    color: 'primary.main',
    fontSize: 12,
  },
  box: {
    width: '100%',
    display: 'flex',
    minHeight: CHART_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartWrapper: {
    width: '100%',
    height: CHART_HEIGHT,
  },
  emptyState: {
    width: '100%',
    height: CHART_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noDataText: {
    color: 'text.secondary',
    fontSize: 11,
  },
  tooltip: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 12px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    gap: 2,
    maxWidth: 200,
  },
  tooltipTitle: {
    fontSize: 11,
    fontWeight: 500,
    color: 'text.primary',
  },
  tooltipValue: {
    fontSize: 11,
    color: 'error.main',
    fontWeight: 500,
  },
  tooltipDetail: {
    fontSize: 10,
    color: 'text.secondary',
  },
}
